import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';
import Hero from './components/Hero';
import Problem from './components/Problem';
import Solution from './components/Solution';
import HowItWorks from './components/HowItWorks';
import Offer from './components/Offer';
import SocialProof from './components/SocialProof';
import FAQ from './components/FAQ';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${siteUrl}/#organization`,
        name: 'Brightworks Intel',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/88011f2529662ad5fc55de36b29bb3e8/c752df5304cc3a404ffb6f7b9d3de882.png'
        },
        description: 'UK analytics agency helping SMBs attract higher-margin work through digital presence analysis and strategic insights.',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'GB'
        },
        areaServed: {
          '@type': 'Country',
          name: 'United Kingdom'
        }
      },
      {
        '@type': 'WebSite',
        '@id': `${siteUrl}/#website`,
        url: siteUrl,
        name: 'Brightworks Intel',
        publisher: {
          '@id': `${siteUrl}/#organization`
        },
        inLanguage: 'en-GB'
      },
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/#webpage`,
        url: siteUrl,
        name: 'Brightworks Intel | Get More High-Value Jobs UK',
        description: 'Stop chasing leads. Start attracting the right ones. Free digital presence review for UK service businesses. See what\'s working and get strategies to attract higher-paying customers.',
        isPartOf: {
          '@id': `${siteUrl}/#website`
        },
        about: {
          '@id': `${siteUrl}/#organization`
        },
        inLanguage: 'en-GB'
      },
      {
        '@type': 'Service',
        '@id': `${siteUrl}/#service`,
        name: 'Digital Presence Review',
        description: 'Free digital presence review including website performance assessment, Google Business Profile audit, content and branding review, and clear action plan to attract better customers.',
        provider: {
          '@id': `${siteUrl}/#organization`
        },
        areaServed: {
          '@type': 'Country',
          name: 'United Kingdom'
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'GBP',
          description: 'Free Digital Presence Review - delivered within 48 hours'
        }
      },
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What do you need from me?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Nothing to start. We review your publicly available information first—your website, Google Business Profile, and online presence. No logins, no access required.'
            }
          },
          {
            '@type': 'Question',
            name: 'What kind of businesses is this for?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'UK service businesses: trades (plumbers, electricians, builders), salons, home improvement companies, and other local service providers who want to attract higher-value customers.'
            }
          },
          {
            '@type': 'Question',
            name: 'What happens after the free review?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'We\'ll walk you through our findings in a clear, jargon-free report. No obligation. If you want ongoing support, we offer monthly insights from £150/month—cancel anytime.'
            }
          }
        ]
      }
    ]
  };

  /* -------------------------------------------------------------------------
   * Scroll listener – moved to useEffect (the correct hook for side‑effects)
   * ----------------------------------------------------------------------- */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Cleanup on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // run once on mount

  /* -------------------------------------------------------------------------
   * Smooth scroll to the offer section
   * ----------------------------------------------------------------------- */
  const scrollToForm = () => {
    const formSection = document.getElementById('offer-section');
    if (!formSection) {
      console.warn('Element with id "offer-section" not found.');
      return;
    }
    formSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="Brightworks Intel | Get More High-Value Jobs UK"
        description="Stop chasing leads. Start attracting the right ones. Free digital presence review for UK service businesses. See what's working and get strategies to attract higher-paying customers."
        keywords="digital presence review, UK service businesses, business analytics, attract better customers, higher-margin work"
        canonical={siteUrl}
        schema={schema}
      />
      
      <div className="min-h-screen bg-white">
        {/* Fixed Header */}
        <header
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-[#1A1F36] ${
            isScrolled ? 'shadow-md' : ''
          }`}
        >
          <div className="max-w-[1400px] mx-auto px-6 h-[60px] flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/88011f2529662ad5fc55de36b29bb3e8/c752df5304cc3a404ffb6f7b9d3de882.png"
                alt="Brightworks Intel"
                className="h-8 w-auto"
              />
            </div>
            <button
              onClick={scrollToForm}
              className="px-6 py-2.5 bg-[#FF6B35] text-white rounded-full font-medium hover:bg-[#E85A2A] hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Review
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <Hero onCTAClick={scrollToForm} />

        {/* Problem Section */}
        <Problem />

        {/* Solution Section */}
        <Solution />

        {/* How It Works Section */}
        <HowItWorks />

        {/* Offer Section */}
        <Offer />

        {/* Social Proof Section */}
        <SocialProof />

        {/* FAQ Section */}
        <FAQ />

        {/* Final CTA Section */}
        <FinalCTA onCTAClick={scrollToForm} />

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
}
